import { useState, useRef, useEffect, useCallback, type FormEvent } from 'react';
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles, Copy, Check } from 'lucide-react';
import { aiChatService, type ChatMessage, type ExperimentContext } from '@/services/aiChatService';
import api from '@/services/api';

/** Lightweight markdown-to-HTML for AI responses */
function renderMarkdown(text: string): string {
  return text
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="ai-inline-code">$1</code>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Unordered lists
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>[\s\S]*?<\/li>)/g, '<ul>$1</ul>')
    // Clean up nested <ul> tags
    .replace(/<\/ul>\s*<ul>/g, '')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Headers
    .replace(/^### (.+)$/gm, '<h4>$1</h4>')
    .replace(/^## (.+)$/gm, '<h3>$1</h3>')
    // Paragraphs (double newline)
    .replace(/\n\n/g, '</p><p>')
    // Single newlines within paragraphs
    .replace(/\n/g, '<br/>')
    // Wrap in paragraph
    .replace(/^(.+)/, '<p>$1</p>')
    .replace(/<p><\/p>/g, '');
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button className="ai-copy-btn" onClick={handleCopy} title="Copy response">
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

interface Props {
  experimentName?: string;
  partName?: string;
  courseId?: string;
  experimentId?: string;
}

export default function AIChatPanel({ experimentName, partName, courseId, experimentId }: Props) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [checkedStatus, setCheckedStatus] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Check if chat is enabled by admin
  useEffect(() => {
    api.get<{ enabled: boolean }>('/ai/chat-status').then(res => {
      setChatEnabled(res.data?.enabled !== false);
      setCheckedStatus(true);
    }).catch(() => setCheckedStatus(true));
  }, []);

  const experimentContext: ExperimentContext = {
    experimentName,
    partName,
    courseId,
    experimentId,
  };

  // Reset messages when experiment changes
  useEffect(() => {
    setMessages([]);
    setError(null);
  }, [experimentId, partName]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  const handleSend = useCallback(async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    setInput('');
    setError(null);

    const userMsg: ChatMessage = { role: 'user', content: msg };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const result = await aiChatService.sendMessage(newMessages, experimentContext);
      if (result.success && result.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
      } else {
        setError(result.response || result.error || 'No response from AI');
      }
    } catch {
      setError('Failed to connect to AI service');
    }

    setLoading(false);
  }, [input, loading, messages, experimentContext]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const suggestions = [
    'Explain this step',
    'What\'s the chemistry behind this?',
    'Help with calculations',
    'What should I observe?',
  ];

  // Don't render anything if chat is disabled by admin
  if (checkedStatus && !chatEnabled) return null;

  return (
    <>
      {/* Toggle Button */}
      {!open && (
        <button className="ai-chat-toggle" onClick={() => setOpen(true)} title="Lab Assistant">
          <MessageCircle size={22} />
          <Sparkles size={12} className="ai-chat-sparkle" />
        </button>
      )}

      {/* Overlay */}
      {open && <div className="ai-chat-overlay" onClick={() => setOpen(false)} />}

      {/* Panel */}
      <div className={`ai-chat-panel ${open ? 'open' : ''}`}>
        {/* Header */}
        <div className="ai-chat-header">
          <div className="ai-chat-header-left">
            <Bot size={20} />
            <div>
              <h3>Lab Assistant</h3>
              {experimentName && <span className="ai-chat-context">{experimentName}</span>}
            </div>
          </div>
          <button className="ai-chat-close" onClick={() => setOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {/* Messages */}
        <div className="ai-chat-messages">
          {messages.length === 0 && !loading && (
            <div className="ai-chat-welcome">
              <Bot size={36} />
              <p>Hi! I'm your lab assistant. Ask me anything about this experiment.</p>
              <div className="ai-chat-suggestions">
                {suggestions.map((s, i) => (
                  <button key={i} className="ai-chat-suggestion" onClick={() => handleSend(s)}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`ai-chat-msg ${msg.role}`}>
              <div className="ai-chat-msg-icon">
                {msg.role === 'user' ? <User size={14} /> : <Bot size={14} />}
              </div>
              <div className="ai-chat-msg-bubble">
                {msg.role === 'assistant' ? (
                  <div
                    className="ai-chat-msg-content ai-md"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                  />
                ) : (
                  <div className="ai-chat-msg-content">{msg.content}</div>
                )}
                {msg.role === 'assistant' && (
                  <CopyButton text={msg.content} />
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="ai-chat-msg assistant">
              <div className="ai-chat-msg-icon"><Bot size={14} /></div>
              <div className="ai-chat-msg-content typing">
                <span /><span /><span />
              </div>
            </div>
          )}

          {error && (
            <div className="ai-chat-error">{error}</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form className="ai-chat-input-area" onSubmit={(e: FormEvent) => { e.preventDefault(); handleSend(); }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this experiment..."
            rows={1}
            disabled={loading}
          />
          <button type="submit" disabled={!input.trim() || loading} className="ai-chat-send">
            {loading ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
          </button>
        </form>
      </div>

      <style>{`
        .ai-chat-toggle {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--primary, #FF5A36), var(--secondary, #00A86B));
          color: white;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.3);
          z-index: 998;
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .ai-chat-toggle:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 24px rgba(0,0,0,0.4);
        }
        .ai-chat-sparkle {
          position: absolute;
          top: 8px;
          right: 8px;
          color: #FFD700;
        }

        .ai-chat-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.3);
          z-index: 999;
        }

        .ai-chat-panel {
          position: fixed;
          top: 0;
          right: 0;
          width: 380px;
          height: 100vh;
          background: var(--bg-surface, #2C2C2C);
          border-left: 1px solid var(--border, #4A4A4A);
          display: flex;
          flex-direction: column;
          z-index: 1000;
          transform: translateX(100%);
          transition: transform 0.3s ease;
          box-shadow: -4px 0 20px rgba(0,0,0,0.3);
        }
        .ai-chat-panel.open {
          transform: translateX(0);
        }

        .ai-chat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          background: linear-gradient(135deg, var(--primary, #FF5A36), var(--primary-dark, #E04420));
          color: white;
        }
        .ai-chat-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .ai-chat-header h3 {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
        }
        .ai-chat-context {
          font-size: 11px;
          opacity: 0.8;
        }
        .ai-chat-close {
          background: rgba(255,255,255,0.15);
          border: none;
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ai-chat-close:hover { background: rgba(255,255,255,0.25); }

        .ai-chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ai-chat-welcome {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 30px 20px;
          color: var(--text-secondary, #ccc);
          gap: 12px;
        }
        .ai-chat-welcome p {
          font-size: 14px;
          margin: 0;
          line-height: 1.5;
        }
        .ai-chat-suggestions {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
          margin-top: 8px;
        }
        .ai-chat-suggestion {
          padding: 8px 14px;
          border-radius: 20px;
          border: 1px solid var(--border, #4A4A4A);
          background: var(--bg-elevated, #3A3A3A);
          color: var(--text-secondary, #ccc);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .ai-chat-suggestion:hover {
          border-color: var(--primary, #FF5A36);
          color: var(--primary, #FF5A36);
        }

        .ai-chat-msg {
          display: flex;
          gap: 8px;
          align-items: flex-start;
        }
        .ai-chat-msg.user {
          flex-direction: row-reverse;
        }
        .ai-chat-msg-icon {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: var(--bg-elevated, #3A3A3A);
          color: var(--text-secondary, #ccc);
        }
        .ai-chat-msg.user .ai-chat-msg-icon {
          background: var(--primary, #FF5A36);
          color: white;
        }
        .ai-chat-msg.assistant .ai-chat-msg-icon {
          background: var(--secondary, #00A86B);
          color: white;
        }
        .ai-chat-msg-bubble {
          max-width: 85%;
          position: relative;
        }
        .ai-chat-msg-content {
          padding: 10px 14px;
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.5;
          word-break: break-word;
        }
        .ai-chat-msg.user .ai-chat-msg-content {
          background: var(--primary, #FF5A36);
          color: white;
          border-bottom-right-radius: 4px;
          white-space: pre-wrap;
        }
        .ai-chat-msg.assistant .ai-chat-msg-content {
          background: var(--bg-elevated, #3A3A3A);
          color: var(--text-primary, #fff);
          border-bottom-left-radius: 4px;
        }

        /* Markdown styles inside AI responses */
        .ai-md p { margin: 0 0 8px 0; }
        .ai-md p:last-child { margin-bottom: 0; }
        .ai-md strong { color: var(--primary-light, #FF7B5C); }
        .ai-md em { font-style: italic; opacity: 0.9; }
        .ai-md ul { margin: 6px 0; padding-left: 18px; }
        .ai-md li { margin-bottom: 4px; }
        .ai-md h3, .ai-md h4 { margin: 10px 0 6px; font-size: 14px; color: var(--accent, #FFD700); }
        .ai-md pre {
          background: rgba(0,0,0,0.3);
          padding: 10px 12px;
          border-radius: 6px;
          margin: 8px 0;
          overflow-x: auto;
          font-size: 12px;
        }
        .ai-md code { font-family: monospace; }
        .ai-inline-code {
          background: rgba(255,255,255,0.1);
          padding: 1px 5px;
          border-radius: 3px;
          font-size: 12px;
        }

        /* Copy button */
        .ai-copy-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: none;
          background: rgba(255,255,255,0.1);
          color: var(--text-muted, #999);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }
        .ai-chat-msg-bubble:hover .ai-copy-btn { opacity: 1; }
        .ai-copy-btn:hover { background: rgba(255,255,255,0.2); color: var(--text-primary); }

        .ai-chat-msg-content.typing {
          display: flex;
          gap: 4px;
          padding: 14px 18px;
        }
        .ai-chat-msg-content.typing span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--text-muted, #999);
          animation: chatBounce 1.4s infinite;
        }
        .ai-chat-msg-content.typing span:nth-child(2) { animation-delay: 0.2s; }
        .ai-chat-msg-content.typing span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes chatBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }

        .ai-chat-error {
          padding: 10px 14px;
          background: rgba(239,68,68,0.1);
          border: 1px solid rgba(239,68,68,0.3);
          border-radius: 8px;
          color: #EF4444;
          font-size: 12px;
        }

        .ai-chat-input-area {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          padding: 12px 16px;
          border-top: 1px solid var(--border, #4A4A4A);
          background: var(--bg-elevated, #333);
        }
        .ai-chat-input-area textarea {
          flex: 1;
          padding: 10px 14px;
          border: 2px solid var(--border, #4A4A4A);
          border-radius: 10px;
          background: var(--bg-surface, #2C2C2C);
          color: var(--text-primary, #fff);
          font-size: 13px;
          font-family: inherit;
          resize: none;
          max-height: 100px;
          outline: none;
          transition: border-color 0.2s;
        }
        .ai-chat-input-area textarea:focus {
          border-color: var(--primary, #FF5A36);
        }
        .ai-chat-input-area textarea::placeholder {
          color: var(--text-muted, #999);
        }
        .ai-chat-send {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          border: none;
          background: var(--primary, #FF5A36);
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: opacity 0.2s;
        }
        .ai-chat-send:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ai-chat-send:not(:disabled):hover {
          opacity: 0.9;
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @media (max-width: 480px) {
          .ai-chat-panel { width: 100%; }
        }
      `}</style>
    </>
  );
}
